// Custom hook for photo management
import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../../utils/api.js';

export const usePhotoManagement = () => {
  const [availablePhotos, setAvailablePhotos] = useState([]);
  const [importedPhotos, setImportedPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoLabels, setPhotoLabels] = useState({});

  // Helper function to format date from YYYY-MM-DD to DD/MM/YYYY
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return null;
    
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // Helper function to format date from YYYY-MM-DD to YYYY-MM-DD (admin API format)
  const formatDateToYYYYMMDD = (dateString) => {
    if (!dateString) return null;
    // Input is already in YYYY-MM-DD format from date inputs
    return dateString;
  };

  // Load work diary photos for a specific period
  const loadWorkDiaryPhotos = async (startDate, endDate) => {
    try {
      // Format dates to YYYY-MM-DD for admin API
      const formattedStartDate = formatDateToYYYYMMDD(startDate);
      const formattedEndDate = formatDateToYYYYMMDD(endDate);
      
      if (!formattedStartDate || !formattedEndDate) {
        console.warn('Invalid dates provided for photo loading');
        return;
      }

      console.log(`Loading photos from ${formattedStartDate} to ${formattedEndDate}`);

      const response = await axios.get(`${API}/admin/work-diary-photos`, {
        params: {
          start_date: formattedStartDate,
          end_date: formattedEndDate
        }
      });
      
      if (response.data && Array.isArray(response.data)) {
        const photos = response.data.map(photo => ({
          ...photo,
          imported: false,
          type: 'work-diary',
          // Ensure we have a proper URL field
          url: photo.gcs_url || photo.url
        }));
        setAvailablePhotos(photos);
        console.log(`Successfully loaded ${photos.length} work diary photos`);
      } else {
        setAvailablePhotos([]);
      }
    } catch (error) {
      console.error('Error loading work diary photos:', error);
      setAvailablePhotos([]);
    }
  };

  // Import photos from the selected period
  const importPhotosFromPeriod = async (startDate, endDate) => {
    try {
      // Format dates to YYYY-MM-DD for admin API
      const formattedStartDate = formatDateToYYYYMMDD(startDate);
      const formattedEndDate = formatDateToYYYYMMDD(endDate);
      
      if (!formattedStartDate || !formattedEndDate) {
        console.warn('Invalid dates provided for photo import');
        return;
      }

      console.log(`Importing photos from ${formattedStartDate} to ${formattedEndDate}`);
      
      // Load photos from work diary using correct date format
      await loadWorkDiaryPhotos(startDate, endDate);
      
      console.log('Photos imported successfully from work diary');
      
    } catch (error) {
      console.error('Error importing photos:', error);
    }
  };

  // Get all photos (work diary + user uploaded)
  const getAllPhotos = () => {
    return [
      ...availablePhotos.map(photo => ({ ...photo, type: 'work-diary' })),
      ...importedPhotos.map(photo => ({ ...photo, type: 'user-uploaded' }))
    ];
  };

  // Toggle photo selection
  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => {
      const isSelected = prev.includes(photoId);
      if (isSelected) {
        // Remove from selection
        return prev.filter(id => id !== photoId);
      } else {
        // Add to selection
        return [...prev, photoId];
      }
    });
  };

  // Update photo label
  const updatePhotoLabel = (photoId, field, value) => {
    setPhotoLabels(prev => ({
      ...prev,
      [photoId]: {
        ...prev[photoId],
        [field]: value
      }
    }));
  };

  // Clear photo selections
  const clearPhotoSelections = () => {
    setSelectedPhotos([]);
    setPhotoLabels({});
  };

  // Remove imported photo
  const removeImportedPhoto = (photoId) => {
    setImportedPhotos(prev => prev.filter(photo => photo.id !== photoId));
    setSelectedPhotos(prev => prev.filter(id => id !== photoId));
    setPhotoLabels(prev => {
      const newLabels = { ...prev };
      delete newLabels[photoId];
      return newLabels;
    });
  };

  // Add user uploaded photos
  const addUserUploadedPhotos = (newPhotos) => {
    const photosWithIds = newPhotos.map(photo => ({
      ...photo,
      id: photo.id || `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'user-uploaded',
      imported: true
    }));
    
    setImportedPhotos(prev => [...prev, ...photosWithIds]);
  };

  // Reorder imported photos
  const reorderImportedPhotos = (reorderedPhotos) => {
    setImportedPhotos(reorderedPhotos);
  };

  return {
    // Photo State
    availablePhotos,
    setAvailablePhotos,
    importedPhotos,
    setImportedPhotos,
    selectedPhotos,
    setSelectedPhotos,
    photoLabels,
    setPhotoLabels,
    
    // Photo Functions
    loadWorkDiaryPhotos,
    importPhotosFromPeriod,
    getAllPhotos,
    togglePhotoSelection,
    updatePhotoLabel,
    clearPhotoSelections,
    removeImportedPhoto,
    addUserUploadedPhotos,
    reorderImportedPhotos,
    
    // Utility Functions
    formatDateToDDMMYYYY
  };
};